import React, { useEffect, useState } from "react";
import api from "../../services/api";

const money = value =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(value || 0));

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #fee2e2",
  borderRadius: 18,
  padding: 18,
  boxShadow: "0 10px 30px rgba(239, 68, 68, 0.08)"
};

const CommercialAlertsDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);

  const loadData = async () => {
    try {
      setLoading(true);

      const month = currentMonth();

      const [auditResponse, goalsResponse] = await Promise.all([
        api.get("/dashboard/seller-audit"),
        api.get(`/dashboard/seller-goals?month=${month}`)
      ]);

      const audit = auditResponse.data || {};
      const goals = goalsResponse.data || {};

      const nextAlerts = [];

      if (Number(audit?.summary?.stoppedTickets || 0) > 0) {
        nextAlerts.push({
          type: "critical",
          title: "Leads parados",
          message: `${audit.summary.stoppedTickets} atendimento(s) estão parados há mais de 30 minutos.`,
          action: "Prioridade máxima: revisar a lista de leads críticos."
        });
      }

      if (Number(audit?.summary?.unansweredTickets || 0) > 0) {
        nextAlerts.push({
          type: "danger",
          title: "Leads sem resposta",
          message: `${audit.summary.unansweredTickets} lead(s) ainda não receberam resposta.`,
          action: "Cobrar resposta imediata dos vendedores responsáveis."
        });
      }

      const sellersBelowGoal = (goals.sellers || []).filter(
        seller => seller.hasGoal && !seller.achieved
      );

      if (sellersBelowGoal.length > 0) {
        nextAlerts.push({
          type: "warning",
          title: "Vendedores abaixo da meta",
          message: `${sellersBelowGoal.length} vendedor(es) ainda não bateram a meta do mês.`,
          action: `Falta vender ${money(goals?.summary?.totalMissing)} no total.`
        });
      }

      const sellersWithoutGoal = (goals.sellers || []).filter(
        seller => !seller.hasGoal
      );

      if (sellersWithoutGoal.length > 0) {
        nextAlerts.push({
          type: "info",
          title: "Vendedores sem meta",
          message: `${sellersWithoutGoal.length} vendedor(es) estão sem meta cadastrada.`,
          action: "Defina metas para todos para acompanhar performance real."
        });
      }

      if (Number(goals?.summary?.requiredDailyAverage || 0) > 0) {
        nextAlerts.push({
          type: "money",
          title: "Média diária necessária",
          message: `Para bater a meta, o time precisa vender ${money(goals.summary.requiredDailyAverage)} por dia.`,
          action: "Use este número como meta diária de cobrança."
        });
      }

      if (nextAlerts.length === 0) {
        nextAlerts.push({
          type: "success",
          title: "Tudo em ordem",
          message: "Nenhum alerta comercial crítico encontrado agora.",
          action: "Continue acompanhando os indicadores diariamente."
        });
      }

      setAlerts(nextAlerts);
    } catch (err) {
      console.error("Erro ao carregar alertas comerciais:", err);
      setAlerts([
        {
          type: "danger",
          title: "Erro ao carregar alertas",
          message: "Não foi possível carregar os alertas comerciais.",
          action: "Verifique a conexão ou atualize a página."
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const colorByType = type => {
    if (type === "success") return "#16a34a";
    if (type === "warning") return "#f59e0b";
    if (type === "info") return "#2563eb";
    if (type === "money") return "#7c3aed";
    return "#dc2626";
  };

  return (
    <div style={{ marginTop: 24, marginBottom: 32, gridColumn: "1 / -1", width: "100%" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #111827 0%, #7f1d1d 55%, #dc2626 100%)",
          borderRadius: 24,
          padding: 24,
          color: "#fff",
          marginBottom: 18,
          boxShadow: "0 20px 45px rgba(220, 38, 38, 0.22)"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 13, opacity: 0.85, fontWeight: 700 }}>
              Central de Alertas
            </div>

            <h2 style={{ margin: "6px 0 4px", fontSize: 28, fontWeight: 900 }}>
              Alertas Comerciais
            </h2>

            <div style={{ opacity: 0.9 }}>
              Acompanhe riscos de venda, leads parados, metas e ações urgentes.
            </div>
          </div>

          <button
            onClick={loadData}
            disabled={loading}
            style={{
              border: 0,
              borderRadius: 12,
              padding: "11px 18px",
              fontWeight: 800,
              cursor: "pointer",
              background: "#fff",
              color: "#b91c1c",
              alignSelf: "end"
            }}
          >
            {loading ? "Atualizando..." : "Atualizar alertas"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
        {alerts.map((alert, index) => (
          <div key={`${alert.title}-${index}`} style={cardStyle}>
            <div
              style={{
                color: colorByType(alert.type),
                fontSize: 13,
                fontWeight: 900,
                marginBottom: 6
              }}
            >
              {alert.type === "critical" ? "URGENTE" : "ALERTA"}
            </div>

            <div style={{ color: "#0f172a", fontSize: 20, fontWeight: 900, marginBottom: 8 }}>
              {alert.title}
            </div>

            <div style={{ color: "#475569", fontSize: 14, marginBottom: 10 }}>
              {alert.message}
            </div>

            <div style={{ color: colorByType(alert.type), fontSize: 13, fontWeight: 800 }}>
              {alert.action}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommercialAlertsDashboard;
